import { useCustomer, useGoogleTagInfo } from './customer';
import config from '../util/load-config';
import { useCallback, useMemo, useState } from 'react';
import { useHasGlobalStatus } from './app';
import { GTAG_INITIALIZED } from 'constants/gtm';
import { GoogleTagInfo } from 'types/third-party/gtm';

export const useGetTokens = (): {
  isAuthenticated: boolean;
  authenticatedToken: string | undefined;
  userToken: string | undefined;
} => {
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const gtagIsInitialized = useHasGlobalStatus(GTAG_INITIALIZED);
  const customer = useCustomer();
  const onLoaded = useCallback(
    (gtm: GoogleTagInfo) => setClientId(gtm.clientId),
    []
  );

  useGoogleTagInfo({ gtagIsInitialized, onLoaded });

  const tokens = useMemo(
    () => ({
      isAuthenticated: !!customer,
      authenticatedToken: customer?.id,
      userToken: clientId?.replace('.', '-'),
    }),
    [clientId, customer]
  );

  return tokens;
};

export const usePostInsights = () => {
  const { isAuthenticated, authenticatedToken, userToken } = useGetTokens();
  const postInsights = useCallback(
    async ({
      queryID,
      objectID,
      index,
      eventType,
      eventName,
      position,
    }: {
      queryID: string | null;
      objectID: string | null;
      index: string | null;
      eventType: 'view' | 'click' | 'conversion';
      eventName: string;
      position?: number;
    }) => {
      const response = await fetch(config.algolia.insightsUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'x-algolia-api-key': config.algolia.apiKey,
          'x-algolia-application-id': config.algolia.appId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          events: [
            {
              eventType,
              eventName,
              index,
              queryID,
              ...(isAuthenticated
                ? { authenticatedUserToken: authenticatedToken }
                : {}),
              userToken,
              objectIDs: [objectID],
              ...(position ? { positions: [position] } : {}),
            },
          ],
        }),
      });
      return response.json();
    },
    [authenticatedToken, isAuthenticated, userToken]
  );
  return postInsights;
};
